import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	ReactNode,
} from "react";
import {
	CampaignDetails,
	AudienceTargeting,
	CreativeFiles,
	ContactInfo,
} from "@/api/campaign";
import { useAuth } from "@/contexts/AuthContext";

interface CampaignContextType {
	campaignDetails: CampaignDetails | null;
	audienceTargeting: AudienceTargeting | null;
	creativeFiles: CreativeFiles | null;
	contactInfo: ContactInfo | null;
	setCampaignDetails: (details: CampaignDetails) => void;
	setAudienceTargeting: (targeting: AudienceTargeting) => void;
	setCreativeFiles: (files: CreativeFiles) => void;
	setContactInfo: (info: ContactInfo) => void;
	clearCampaign: () => void;
	resetCampaign: () => void;
	saveDraft: () => void;
	loadDraft: () => void;
	validateCampaignState: () => boolean;
}

const CAMPAIGN_DRAFT_KEY = "campaign_draft";
const CAMPAIGN_DRAFT_ID_KEY = "campaign_draft_id";
const CAMPAIGN_LAST_ACTIVE = "campaign_last_active";
const CAMPAIGN_TIMEOUT = 30 * 60 * 1000; // 30 minutes

const CampaignContext = createContext<CampaignContextType | null>(null);

function CampaignProviderContent({ children }: { children: ReactNode }) {
	const [campaignDetails, setCampaignDetails] =
		useState<CampaignDetails | null>(null);
	const [audienceTargeting, setAudienceTargeting] =
		useState<AudienceTargeting | null>(null);
	const [creativeFiles, setCreativeFiles] = useState<CreativeFiles | null>(
		null
	);
	const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
	const { isAuthenticated } = useAuth();

	const validateCampaignState = useCallback(() => {
		const lastActive = localStorage.getItem(CAMPAIGN_LAST_ACTIVE);
		if (!lastActive) return false;

		const lastActiveTime = parseInt(lastActive, 10);
		const now = Date.now();

		// Check if campaign state is too old
		if (now - lastActiveTime > CAMPAIGN_TIMEOUT) {
			clearCampaign();
			return false;
		}

		return true;
	}, []);

	const clearCampaign = useCallback(() => {
		setCampaignDetails(null);
		setAudienceTargeting(null);
		setCreativeFiles(null);
		setContactInfo(null);
		localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
		localStorage.removeItem(CAMPAIGN_LAST_ACTIVE);
	}, []);

	// Reset campaign state and clear all drafts
	const resetCampaign = useCallback(() => {
		clearCampaign();
		localStorage.removeItem(CAMPAIGN_DRAFT_ID_KEY);
	}, [clearCampaign]);

	// Save current campaign state to localStorage
	const saveDraft = useCallback(() => {
		if (
			!campaignDetails &&
			!audienceTargeting &&
			!creativeFiles &&
			!contactInfo
		) {
			return;
		}

		const draft = {
			campaignDetails,
			audienceTargeting,
			creativeFiles,
			contactInfo,
			lastUpdated: new Date().toISOString(),
		};

		localStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(draft));
		localStorage.setItem(CAMPAIGN_LAST_ACTIVE, Date.now().toString());
	}, [campaignDetails, audienceTargeting, creativeFiles, contactInfo]);

	// Load campaign state from localStorage
	const loadDraft = useCallback(() => {
		try {
			if (!validateCampaignState()) {
				return;
			}

			const savedDraft = localStorage.getItem(CAMPAIGN_DRAFT_KEY);
			if (savedDraft) {
				const draft = JSON.parse(savedDraft);
				setCampaignDetails(draft.campaignDetails);
				setAudienceTargeting(draft.audienceTargeting);
				setCreativeFiles(draft.creativeFiles);
				setContactInfo(draft.contactInfo);
				localStorage.setItem(CAMPAIGN_LAST_ACTIVE, Date.now().toString());
			}
		} catch (error) {
			console.error("Error loading campaign draft:", error);
			// If there's an error loading the draft, clear it
			clearCampaign();
		}
	}, [validateCampaignState, clearCampaign]);

	// Load draft when component mounts and user is authenticated
	useEffect(() => {
		if (isAuthenticated) {
			loadDraft();
		}
	}, [isAuthenticated, loadDraft]);

	// Auto-save campaign state when it changes
	useEffect(() => {
		if (isAuthenticated) {
			saveDraft();
		}
	}, [isAuthenticated, saveDraft]);

	// Check campaign state validity periodically
	useEffect(() => {
		if (isAuthenticated) {
			const validityCheck = setInterval(() => {
				if (!validateCampaignState()) {
					clearCampaign();
				}
			}, 60000); // Check every minute

			return () => clearInterval(validityCheck);
		}
	}, [isAuthenticated, validateCampaignState, clearCampaign]);

	const contextValue = {
		campaignDetails,
		audienceTargeting,
		creativeFiles,
		contactInfo,
		setCampaignDetails,
		setAudienceTargeting,
		setCreativeFiles,
		setContactInfo,
		clearCampaign,
		resetCampaign,
		saveDraft,
		loadDraft,
		validateCampaignState,
	};

	return (
		<CampaignContext.Provider value={contextValue}>
			{children}
		</CampaignContext.Provider>
	);
}

export function CampaignProvider({ children }: { children: ReactNode }) {
	return <CampaignProviderContent>{children}</CampaignProviderContent>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCampaign(): CampaignContextType {
	const context = useContext(CampaignContext);
	if (!context) {
		throw new Error("useCampaign must be used within a CampaignProvider");
	}
	return context;
}
